// src/pages/RefundPolicy.jsx
import React from "react";

const PAGE_GRADIENT =
  "radial-gradient(circle at top, #26266b 0, #050514 55%, #000 100%)";

export default function RefundPolicy() {
  return (
    <div
      className="min-h-screen text-slate-100 px-4 py-10"
      style={{ background: PAGE_GRADIENT }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-pink-400/80 mb-2">
            Billing
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2">
            Refund Policy
          </h1>
          <p className="text-sm text-slate-400">
            Last updated:{" "}
            <span className="font-medium text-slate-200">
              27 November 2025
            </span>
          </p>
        </header>

        <div className="space-y-8 text-sm md:text-base leading-relaxed text-slate-200">
          {/* 1. Intro */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              1. Overview
            </h2>
            <p className="mb-2">
              This Refund Policy explains when customers of{" "}
              <span className="font-semibold">PixelAds.ai</span> may be
              eligible for a refund of subscription fees. It is part of our
              Terms and Conditions and should be read together with them.
            </p>
            <p>
              Because PixelAds processes digital assets and connects to
              third-party AI vendors, we need clear rules about when a refund is
              possible and when it is not.
            </p>
          </section>

          {/* 2. Who this applies to */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              2. Who this policy applies to
            </h2>
            <p>
              This policy applies to paid subscriptions and plan upgrades for
              PixelAds (for example, Starter, Pro, Business), whether billed
              monthly or yearly. It does not cover any third-party fees, such as
              Shopify subscription costs or advertising spend on platforms like
              Meta or Google.
            </p>
          </section>

          {/* 3. General rule */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              3. General refund rule
            </h2>
            <p className="mb-2">
              We want you to be happy with PixelAds, but we also need a fair
              policy for a usage-based product.
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>
                We generally do not offer refunds once you have started using
                the paid features of your plan (for example, generating images
                or videos beyond the initial trial or starter usage that comes
                with your account).
              </li>
              <li>
                You can cancel your subscription at any time. When you cancel,
                you keep access until the end of your current billing period and
                you are not charged again.
              </li>
            </ul>
            <p className="mt-2">
              When you upgrade your plan or switch between monthly and yearly
              billing, Stripe may apply prorated charges or credits to your
              invoices. These adjustments are handled automatically by Stripe
              and are separate from refunds under this policy.
            </p>
          </section>

          {/* 4. Money-back eligibility window */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              4. Eligibility for a refund
            </h2>
            <p className="mb-2">
              We may consider a refund for your initial subscription payment if
              all of the following conditions are met:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>
                Your request is made within{" "}
                <span className="font-semibold">14 days</span> of the initial
                payment for the current subscription term.
              </li>
              <li>
                You have not used the paid features of your plan beyond the
                initial trial or starter usage that comes with your account. In
                practice this means:
                <ul className="list-disc list-inside ml-5 mt-1 space-y-1 text-slate-200/90">
                  <li>
                    You did not generate more than a small number of paid
                    assets (for example, images and videos) that consume plan
                    quotas; and
                  </li>
                  <li>
                    You did not exceed the free trial or starter allowances
                    communicated in the app or on the pricing page.
                  </li>
                </ul>
              </li>
              <li>
                You are not requesting a refund repeatedly or in a way that
                could reasonably be considered abusive or fraudulent.
              </li>
            </ul>
            <p className="mt-2">
              We reserve the right to check usage logs to determine whether paid
              features have been used.
            </p>
          </section>

          {/* 5. Non-refundable situations */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              5. Non-refundable situations
            </h2>
            <p className="mb-2">
              In the following situations, payments are normally not eligible
              for a refund:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>
                You have generated a meaningful number of images or videos with
                PixelAds, including via Phot.AI or TopView integrations.
              </li>
              <li>
                You are past the 14-day window for initial subscription
                payments.
              </li>
              <li>
                You are requesting a refund for a renewal period where the plan
                continued and you could use the Service.
              </li>
              <li>
                You changed your mind after using the Service as described but
                did not encounter a technical issue we could not reasonably fix.
              </li>
              <li>
                You purchased add-ons or one-off services (for example, extra
                images, videos, or product pages) that have already been
                processed. These are normally non-refundable, even if you have
                not fully used the additional capacity.
              </li>
            </ul>
          </section>

          {/* 6. Yearly plans */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              6. Yearly plans
            </h2>
            <p className="mb-2">
              Yearly plans are billed as a single payment for a 12 month period
              (often with a discount compared to monthly billing).
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>
                If you cancel a yearly plan, you keep access until the end of
                the paid period and the plan will not renew.
              </li>
              <li>
                Outside of the 14-day eligibility window described above, yearly
                fees are normally non-refundable.
              </li>
            </ul>
          </section>

          {/* 7. How to request a refund */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              7. How to request a refund
            </h2>
            <p className="mb-2">
              If you believe you are eligible for a refund under this policy,
              please contact us from the email address associated with your
              account and include:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>Your full name and PixelAds account email</li>
              <li>The type of plan you purchased (Starter, Pro, Business)</li>
              <li>
                The date of your payment and billing frequency (monthly or
                yearly)
              </li>
              <li>
                A short explanation of the issue and why you are requesting a
                refund
              </li>
            </ul>
            <p>
              Send your request to{" "}
              <span className="font-mono">contact@pixelads.ai</span>. We review
              each request in good faith and will usually respond within a
              reasonable timeframe.
            </p>
          </section>

          {/* 8. EU consumer rights */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              8. EU consumer rights
            </h2>
            <p>
              If you are a consumer in the European Union, you may have
              additional statutory rights that go beyond this policy. Nothing in
              this Refund Policy is intended to limit rights you have under
              mandatory consumer protection laws.
            </p>
          </section>

          {/* 9. Changes */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              9. Changes to this policy
            </h2>
            <p>
              We may update this Refund Policy from time to time. When we do, we
              will update the "Last updated" date at the top and, where
              appropriate, notify you in the app or by email. If you continue to
              use PixelAds after changes take effect, you accept the updated
              policy.
            </p>
          </section>

          {/* 10. Contact */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              10. Contact
            </h2>
            <p>
              For billing or refund questions, contact{" "}
              <span className="font-mono">contact@pixelads.ai</span>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
